import React, { Fragment, useState, useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import { Box, IconButton, TextField, InputAdornment } from "@mui/material";
import { convertINRDecimals, setCryptoDecimals } from "../utils";
import FlexButton from "./FlexButton";
import { colors } from "../assets/colors";
import { apiRouterCall } from "../api/services";
import SuccessModal from "./modals/SuccessModal";
import ScreenLoader from "./screenLoader";
import ErrorModal from "./modals/ErrorModal";
import moment from "moment";
import toast from "react-hot-toast";
import usePaymentOptions from "../hooks/usePaymentOptions";
import PaymentModeModal from "./modals/PaymentModeModal";
import ConfirmationModal from "./modals/ConfirmationModal";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <IconButton>
        <AddIcon sx={{ fontSize: "15px" }} />
      </IconButton>
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  //   flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

export default function CustomAccordian({
  item,
  type,
  callBack,
  callBackMain,
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [isSuccessfull, setIsSuccessfull] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setIsError] = useState("");
  const [isValidAmount, setIsValidAmount] = useState(true);
  //function to apply for the loan
  const loadnApplyHandler = async (symbol, discountAmount) => {
    try {
      if (amount <= 0) {
        setIsError("Amount should be greater than 0.");
        return;
      }
      setIsProcessing(true);
      const data =
        type === "leverage"
          ? {
              amount: amount,
            }
          : {
              amount: amount,
              symbol: symbol,
              discountAmount: Number(amount) - Number(discountAmount),
            };
      const response = await apiRouterCall({
        method: "POST",
        endPoint: type === "leverage" ? "applyLoan" : "payLoan",
        data: data,
      });
      if (response.data.responseCode === 200) {
        setIsSuccessfull(true);
      }
      setIsProcessing(false);
    } catch (error) {
      console.log(error);
      setIsProcessing(false);
      setIsError(error?.response?.data?.responseMessage);
    }
  };

  const availabeAmount = useMemo(() => {
    if (type === "leverage") {
      return (
        Number(item.inrFreezBalance) * Number(item.multiplier) -
        Number(item.inrFreezBalance)
      );
    } else {
      return 0;
    }
  }, [item, type]);

  const platformFee = useMemo(() => {
    if (amount === "") {
      return 0;
    }
    if (type === "leverage") {
      const fee = (Number(amount) * Number(item.platformFee)) / 100;

      return fee < 10 ? 10 : fee;
    } else {
      return 0;
    }
  }, [item, type, amount]);
  return (
    <Fragment>
      <Accordion
        expanded={open}
        onClick={() => setOpen(!open)}
        sx={{
          border:
            type === "leverage" || type === "loan"
              ? `2px solid ${colors.btnGold}`
              : "",
        }}
      >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Typography variant="h6">
            {type === "faq"
              ? item.title
              : type === "leverage"
              ? "Available Leverage"
              : type === "loan"
              ? "Active Leverage"
              : item.planType}
          </Typography>
        </AccordionSummary>
        <AccordionDetails onClick={(event) => event.stopPropagation()}>
          {type === "faq" && (
            <Typography variant="body2">{item.description}</Typography>
          )}
          {type === "tranches" && (
            <Box width="100%">
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  ROI:
                </Typography>
                <Typography variant="body2">{`${item.interest}%`}</Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Min Balance:
                </Typography>
                <Typography variant="body2">
                  {item && `${setCryptoDecimals(item.minQty)} TARAL`}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Max Balance:
                </Typography>
                <Typography variant="body2">
                  {item && `${setCryptoDecimals(item.maxQty)} TARAL`}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Min INR:
                </Typography>
                <Typography variant="body2">
                  {item && `${convertINRDecimals(item.minInrQty)}`}
                </Typography>
              </Box>
              {/* <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Max INR:
                </Typography>
                <Typography variant="body2">
                  {item && `${convertINRDecimals(item.maxInrQty)}`}
                </Typography>
              </Box> */}
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Multiplier:
                </Typography>
                <Typography variant="body2">
                  {item && `${item.multiplier}X`}
                </Typography>
              </Box>
            </Box>
          )}
          {type === "leverage" && (
            <Box width="100%">
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  INR Balance:
                </Typography>
                <Typography variant="body2">
                  {item && convertINRDecimals(item.inrFreezBalance)}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  TARAL Balance:
                </Typography>
                <Typography variant="body2">
                  {item && `${setCryptoDecimals(item.taralFreezBalance)} TARAL`}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Available Leverage:
                </Typography>
                <Typography variant="body2">
                  {availabeAmount && convertINRDecimals(availabeAmount)}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Platform Fee / Day:
                </Typography>
                <Typography variant="body2">
                  {platformFee && convertINRDecimals(platformFee)}
                </Typography>
              </Box>
              <CommonInputField
                type={type}
                item={item}
                amount={amount}
                setAmount={setAmount}
                onClick={loadnApplyHandler}
                isValidAmount={isValidAmount}
                setIsValidAmount={setIsValidAmount}
                availabeAmount={availabeAmount}
              />
            </Box>
          )}

          {type === "loan" && (
            <Box width={"100%"}>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Loan Amount:
                </Typography>
                <Typography variant="body2">
                  {item && convertINRDecimals(item.amount)}
                </Typography>
              </Box>

              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Paid Amount:
                </Typography>
                <Typography variant="body2">
                  {item && convertINRDecimals(item.paidAmount)}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Platform Fee / Day:
                </Typography>
                <Typography variant="body2">
                  {item && convertINRDecimals(item.i_amount)}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Status:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: item.isActive ? colors.green : colors.btnGold,
                    fontWeight: 600,
                  }}
                >
                  {item && item.isActive ? "Active" : "Completed"}
                </Typography>
              </Box>

              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Date/Time:
                </Typography>
                <Typography variant="body2">
                  {item && moment(item.createdAt).format("lll")}
                </Typography>
              </Box>
              <Box className="displaySpacebetween">
                <Typography variant="body2" fontWeight={500}>
                  Total Due Amount:
                </Typography>
                <Typography variant="body2">
                  {item && convertINRDecimals(item.dueAmount)}
                </Typography>
              </Box>
              <CommonInputField
                type={type}
                item={item}
                amount={amount}
                setAmount={setAmount}
                onClick={(symbol, discountAmount) =>
                  loadnApplyHandler(symbol, discountAmount)
                }
                isValidAmount={isValidAmount}
                setIsValidAmount={setIsValidAmount}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
      {isSuccessfull && (
        <SuccessModal
          open={isSuccessfull}
          close={() => {
            setIsSuccessfull(false);
            setAmount("");
            callBack();
            callBackMain();
          }}
          title={type === "leverage" ? "" : "Transaction Successfull!"}
          description={
            type === "leverage"
              ? `You have successfully applied for ${convertINRDecimals(
                  amount
                )}. You will receive the funds in your wallet soon.`
              : `You have successfully paid ${convertINRDecimals(
                  amount
                )} in your active loan.`
          }
        />
      )}
      {isProcessing && (
        <ScreenLoader
          open={isProcessing}
          close={() => setIsProcessing(false)}
        />
      )}
      {error && (
        <ErrorModal
          open={true}
          close={() => setIsError("")}
          title={"Error!"}
          description={error}
        />
      )}
    </Fragment>
  );
}

const CommonInputField = ({
  onClick,
  amount,
  setAmount,
  type,
  item,
  availabeAmount,
  isValidAmount,
  setIsValidAmount,
}) => {
  const paymentOptions = usePaymentOptions({ module: "WALLET_LEVERAGE" });
  const [isApply, setIsApply] = useState(false);
  const [openPayOptions, setOpenPayOptions] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState({
    amount: "",
    coin: "",
    discount: "",
  });

  return (
    <Box mt={1}>
      {!isApply && (
        <FlexButton
          color="primary"
          title={type === "loan" ? "Pay Now" : "Apply"}
          sx={{ padding: "4px" }}
          fullWidth={true}
          onClick={() => setIsApply(true)}
        />
      )}
      {isApply && (
        <Box className="displayCenter">
          <TextField
            type="number"
            value={amount}
            onChange={(e) => {
              if (e.target.value.length < 10) {
                setAmount(e.target.value);
              }

              if (type === "leverage") {
                if (Number(e.target.value) > Number(availabeAmount)) {
                  setIsValidAmount(false);
                } else {
                  setIsValidAmount(true);
                }
              } else {
                if (Number(e.target.value) > Number(item.dueAmount)) {
                  setIsValidAmount(false);
                } else {
                  setIsValidAmount(true);
                }
              }
            }}
            fullWidth
            placeholder="Enter amount..."
            InputProps={
              type === "loan" && {
                endAdornment: (
                  <InputAdornment position="end">
                    <FlexButton
                      sx={{
                        margin: "5px",
                        padding: "5px",
                      }}
                      onClick={() => setAmount(item.dueAmount)}
                      title="Max"
                      color="primary"
                    />
                  </InputAdornment>
                ),
              }
            }
            error={!isValidAmount && amount !== ""}
            helperText={
              !isValidAmount &&
              amount !== "" &&
              `You can not enter more than ${convertINRDecimals(
                type === "leverage" ? availabeAmount : item?.amount
              )}. `
            }
          />
          <FlexButton
            title={type === "loan" ? "Pay" : "Submit"}
            color="primary"
            disabled={!amount || amount === "" || !isValidAmount}
            sx={{ marginLeft: "10px" }}
            // onClick={onClick}
            onClick={() => {
              if (type === "leverage") {
                onClick();
              } else {
                setOpenPayOptions(true);
              }
            }}
          />
        </Box>
      )}
      {openPayOptions && (
        <PaymentModeModal
          open={openPayOptions}
          options={paymentOptions}
          amount={amount}
          close={() => setOpenPayOptions(false)}
          onSelect={(amount, coin, discount) => {
            setSelectedAmount({
              amount: amount,
              coin: coin,
              discount: discount,
            });

            setIsConfirm(true);
          }}
          type={"levereage"}
        />
      )}
      {isConfirm && (
        <ConfirmationModal
          open={isConfirm}
          close={() => setIsConfirm(false)}
          title={"Confirm Transaction!"}
          description={
            selectedAmount.coin === "INR"
              ? `Are you sure want to pay ${convertINRDecimals(amount)}?`
              : `Are you sure want to pay ${
                  selectedAmount.coin
                } worth of ${convertINRDecimals(selectedAmount.amount)}`
          }
          onClick={() => {
            onClick(selectedAmount.coin, selectedAmount.amount);
            setIsConfirm(false);
          }}
        />
      )}
    </Box>
  );
};
